import maplibregl from 'maplibre-gl';
import LayerManager from './LayerManager';
import { ProductId } from '../types';
import store from '../state';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import { getTileUrl, getModelCoordinates } from '../config';
import ForecastAnimationService from '../services/ForecastAnimationService';

export interface StandardInterpolatedRasterOptions {
  layerId?: string;
  product?: ProductId;
  model: string;
  targetTime?: Date | number | null;
  opacity: number;
  beforeId?: string;
  minzoom?: number;
  maxzoom?: number;
  forcePmtiles?: boolean;
  maxGapHours?: number;
  formatHour?: (dateStr: string, cleanHourStr: string) => string;
}

export abstract class BaseProduct {
  public abstract readonly id: ProductId;
  protected map: maplibregl.Map;
  protected layerManager: LayerManager;
  protected active: boolean = false;
  protected activeRasterConfigs: Map<string, StandardInterpolatedRasterOptions> = new Map();

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    this.map = map;
    this.layerManager = layerManager;
  }

  public activate(): void {
    if (this.active) return;
    this.active = true;
    this.onActivate();
  }

  public deactivate(): void {
    if (!this.active) return;
    this.active = false;
    this.activeRasterConfigs.clear();
    this.onDeactivate();
  }

  public updateTime(date: string, hour: string): void {
    if (!this.active) return;
    this.onTimeUpdate(date, hour);
  }

  public refresh(): void {
    if (!this.active) return;
    const current = store.getState().currentTimeStep;
    if (current) {
      const dateStr = current.toISOString().split('T')[0];
      const hourStr = String(current.getUTCHours()).padStart(2, '0');
      this.onTimeUpdate(dateStr, hourStr);
    }
  }

  /**
   * Renders a continuous (fractional) timestamp for 60 FPS GPU crossfade interpolation.
   * Subclasses that support temporal interpolation override or delegate to refresh(targetTimeMs).
   */
  public renderInterpolated(targetTimeMs: number): void {
    if (!this.active) return;
    const refreshFn = (this as any).refresh;
    if (typeof refreshFn === 'function' && refreshFn.length >= 1) {
      refreshFn.call(this, targetTimeMs);
    }
  }

  /**
   * Resolves bracketing frames (t0, t1, blend) from ManifestTimelineHelper and updates
   * the LayerManager's WebGL crossfade or buffered raster layer.
   */
  protected updateStandardInterpolatedRaster(options: StandardInterpolatedRasterOptions): boolean {
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return false;

    const targetProduct = options.product || this.id;
    const baseLayerId = options.layerId || this.id;
    this.activeRasterConfigs.set(baseLayerId, { ...options, product: targetProduct, layerId: baseLayerId });

    const animCursorMs = state.forecastAnimationPlaying
      ? ForecastAnimationService.getInstance().getCurrentCursorMs()
      : null;
    const timeInput =
      typeof options.targetTime === 'number'
        ? options.targetTime
        : (animCursorMs ?? options.targetTime ?? state.currentTimeStep);
    if (!timeInput) return false;

    const interpolationEnabled =
      typeof __APP_CONFIG__ === 'undefined' ||
      __APP_CONFIG__?.forecast_animation?.interpolation_enabled !== false;
    const defaultMaxGap =
      (typeof __APP_CONFIG__ !== 'undefined' &&
        __APP_CONFIG__?.forecast_animation?.max_interpolation_gap_hours) ||
      6;
    const maxGapHours = interpolationEnabled ? (options.maxGapHours ?? defaultMaxGap) : 0;

    const bracket = ManifestTimelineHelper.getBracketingFrames(
      manifest,
      targetProduct,
      options.model,
      timeInput,
      maxGapHours
    );
    if (!bracket) return false;

    const cleanH0 = bracket.frame0.hourStr.replace(/^Z/i, '');
    const cleanH1 = bracket.frame1.hourStr.replace(/^Z/i, '');
    const h0 = options.formatHour
      ? options.formatHour(bracket.frame0.dateStr, cleanH0)
      : cleanH0;
    const h1 = options.formatHour
      ? options.formatHour(bracket.frame1.dateStr, cleanH1)
      : cleanH1;

    const forcePmtiles = Boolean(options.forcePmtiles);
    const url0 = getTileUrl(manifest, targetProduct, options.model, bracket.frame0.dateStr, h0, forcePmtiles);
    const sameFrame = bracket.frame0.timestamp === bracket.frame1.timestamp;
    const url1 = sameFrame
      ? url0
      : getTileUrl(manifest, targetProduct, options.model, bracket.frame1.dateStr, h1, forcePmtiles);

    this.layerManager.updateInterpolatedRasterLayer(
      baseLayerId,
      url0,
      url1,
      sameFrame ? 0 : bracket.blend,
      options.opacity,
      options.beforeId,
      options.minzoom ?? 0,
      options.maxzoom ?? 5,
      getModelCoordinates(manifest, targetProduct, options.model)
    );
    return true;
  }

  /**
   * Computes the exact (url0, url1) URLs that this active layer will request at targetTimeMs.
   * Used by ForecastAnimationService.preloadHours() so Cycle 1 pre-buffers the exact frame URLs.
   */
  public getPreloadTargetsForTime(targetTimeMs: number): Array<{
    layerId: string;
    product: ProductId;
    model: string;
    url0: string;
    url1: string;
    isSingleImage: boolean;
  }> {
    if (!this.active || this.activeRasterConfigs.size === 0) return [];
    const manifest = store.getState().manifest;
    if (!manifest) return [];

    const interpolationEnabled =
      typeof __APP_CONFIG__ === 'undefined' ||
      __APP_CONFIG__?.forecast_animation?.interpolation_enabled !== false;
    const defaultMaxGap =
      (typeof __APP_CONFIG__ !== 'undefined' &&
        __APP_CONFIG__?.forecast_animation?.max_interpolation_gap_hours) ||
      6;

    const targets: Array<{
      layerId: string;
      product: ProductId;
      model: string;
      url0: string;
      url1: string;
      isSingleImage: boolean;
    }> = [];

    this.activeRasterConfigs.forEach((options, baseLayerId) => {
      const targetProduct = options.product || this.id;
      const maxGapHours = interpolationEnabled ? (options.maxGapHours ?? defaultMaxGap) : 0;
      const bracket = ManifestTimelineHelper.getBracketingFrames(
        manifest,
        targetProduct,
        options.model,
        targetTimeMs,
        maxGapHours
      );
      if (!bracket) return;

      const cleanH0 = bracket.frame0.hourStr.replace(/^Z/i, '');
      const cleanH1 = bracket.frame1.hourStr.replace(/^Z/i, '');
      const h0 = options.formatHour ? options.formatHour(bracket.frame0.dateStr, cleanH0) : cleanH0;
      const h1 = options.formatHour ? options.formatHour(bracket.frame1.dateStr, cleanH1) : cleanH1;

      const forcePmtiles = Boolean(options.forcePmtiles);
      const url0 = getTileUrl(manifest, targetProduct, options.model, bracket.frame0.dateStr, h0, forcePmtiles);
      const sameFrame = bracket.frame0.timestamp === bracket.frame1.timestamp;
      const url1 = sameFrame
        ? url0
        : getTileUrl(manifest, targetProduct, options.model, bracket.frame1.dateStr, h1, forcePmtiles);
      const clean0 = (url0 || '').split('?')[0];
      const isSingleImage = clean0.endsWith('.webp') && !clean0.includes('{z}');

      if (url0) {
        targets.push({
          layerId: baseLayerId,
          product: targetProduct,
          model: options.model,
          url0,
          url1: url1 || url0,
          isSingleImage
        });
      }
    });

    return targets;
  }

  protected abstract onActivate(): void;
  protected abstract onDeactivate(): void;
  protected abstract onTimeUpdate(date: string, hour: string): void;
}
export default BaseProduct;
